// ============================================================================
// PRELIT-POOL-001 — Early Recovery Pool
// ============================================================================
// Live list of accounts that meet Early Recovery eligibility AND have an open
// acct.tbl_case row in pool_status='UNCLAIMED'. Read-only grid; Open and
// Manual Close are gated by RecsuitPermissionGate / surfaced as row actions.
// Default sort: opened_at DESC, currentOutstanding DESC (info-2.txt §1).
//
// No Entity/BU badges (info-2.txt §1).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useEligibilityPool, useRecomputePool } from 'hooks/api/usePreLitigation';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import type {
  EligibleAccount,
  EligiblePoolSearchRequest,
} from 'api/types/preLitigation.types';
import ClaimCaseConfirmDialog from 'components/modals/modules/pre-litigation-module/ClaimCaseConfirmDialog';
import ManualCloseUnclaimedModal from 'components/modals/modules/pre-litigation-module/ManualCloseUnclaimedModal';
import { openCaseDetail } from 'components/modules/pre-litigation-module/openCaseDetail';

interface PreLitigationPoolScreenProps {
  /** Passed by main_layout when rendered as a tabbed module. */
  tabId?: string;
  moduleName?: string;
}

type ViewMode = 'list' | 'grid';

const PRELIT_POOL_MODULE = 'pre-litigation-pool';
const PRELIT_CASE_MODULE = 'pre-litigation-case';

const PreLitigationPoolScreen: React.FC<PreLitigationPoolScreenProps> = ({ tabId }) => {
  const permissions = useRecsuitPermissions();

  // ----- Server-side query params -----
  const [queryParams, setQueryParams] = useState<EligiblePoolSearchRequest>({
    page: 1,
    pageSize: 25,
    sortBy: 'opened_at',
    sortOrder: 'desc',
  });
  const { data, loading, pagination, refetch } = useEligibilityPool(queryParams);
  const recompute = useRecomputePool();

  // ----- View state -----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [gridPage, setGridPage] = useState(0);
  const gridPageSize = 12;

  // Debounced global search → server
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ----- Filter panel state -----
  const [productFilter, setProductFilter] = useState('');
  const [branchCodeFilter, setBranchCodeFilter] = useState('');
  const [dpdMin, setDpdMin] = useState('');
  const [dpdMax, setDpdMax] = useState('');
  const [outstandingMin, setOutstandingMin] = useState('');
  const [outstandingMax, setOutstandingMax] = useState('');
  const [emiCountMin, setEmiCountMin] = useState('');
  const [emiCountMax, setEmiCountMax] = useState('');
  const [emiAmountMin, setEmiAmountMin] = useState('');
  const [emiAmountMax, setEmiAmountMax] = useState('');
  const [securedFilter, setSecuredFilter] = useState<string>(''); // '', 'true', 'false'

  const applyFilters = useCallback(() => {
    const securedBool =
      securedFilter === 'true' ? true : securedFilter === 'false' ? false : undefined;
    setQueryParams(prev => ({
      ...prev,
      productCodes: productFilter ? [productFilter] : undefined,
      dpdMin: dpdMin ? Number(dpdMin) : undefined,
      dpdMax: dpdMax ? Number(dpdMax) : undefined,
      outstandingMin: outstandingMin ? Number(outstandingMin) : undefined,
      outstandingMax: outstandingMax ? Number(outstandingMax) : undefined,
      emiCountMin: emiCountMin ? Number(emiCountMin) : undefined,
      emiCountMax: emiCountMax ? Number(emiCountMax) : undefined,
      emiAmountMin: emiAmountMin ? Number(emiAmountMin) : undefined,
      emiAmountMax: emiAmountMax ? Number(emiAmountMax) : undefined,
      isSecured: securedBool,
      page: 1,
    }));
    setFilterOpen(false);
  }, [
    productFilter, branchCodeFilter,
    dpdMin, dpdMax, outstandingMin, outstandingMax,
    emiCountMin, emiCountMax, emiAmountMin, emiAmountMax,
    securedFilter,
  ]);

  const clearFilters = useCallback(() => {
    setProductFilter('');
    setBranchCodeFilter('');
    setDpdMin(''); setDpdMax('');
    setOutstandingMin(''); setOutstandingMax('');
    setEmiCountMin(''); setEmiCountMax('');
    setEmiAmountMin(''); setEmiAmountMax('');
    setSecuredFilter('');
    setQueryParams({
      page: 1,
      pageSize: queryParams.pageSize ?? 25,
      sortBy: 'opened_at',
      sortOrder: 'desc',
    });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.productCodes?.length) n++;
    if (queryParams.dpdMin != null) n++;
    if (queryParams.dpdMax != null) n++;
    if (queryParams.outstandingMin != null) n++;
    if (queryParams.outstandingMax != null) n++;
    if (queryParams.emiCountMin != null) n++;
    if (queryParams.emiCountMax != null) n++;
    if (queryParams.emiAmountMin != null) n++;
    if (queryParams.emiAmountMax != null) n++;
    if (queryParams.isSecured !== undefined) n++;
    return n;
  }, [queryParams]);

  // ----- Pagination handlers -----
  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // ----- Claim flow -----
  const [claimTarget, setClaimTarget] = useState<EligibleAccount | null>(null);

  const handleClaimClick = useCallback((row: EligibleAccount) => {
    setClaimTarget(row);
  }, []);

  const handleClaimed = useCallback((caseId: string) => {
    setClaimTarget(null);
    openCaseDetail(caseId);
  }, []);

  // ----- Recompute Pool -----
  const handleRecompute = useCallback(async () => {
    setIsRecomputing(true);
    try {
      const result = await recompute.run({ caseTypeCodes: ['PRE_LITIGATION'] });
      const summary = result.data?.byCaseType?.['PRE_LITIGATION'];
      if (summary) {
        notificationService.toast.success(
          `Pool recomputed — ${summary.added} added, ${summary.removed} removed, ${summary.unchanged} unchanged.`,
          5000,
        );
      } else {
        notificationService.toast.success('Pool recompute complete.');
      }
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Recompute failed. Please try again.';
      notificationService.toast.error(msg, 6000);
    } finally {
      setIsRecomputing(false);
    }
  }, [recompute, refetch]);

  // ----- Manual Close (UNCLAIMED rows) -----
  const [manualCloseTarget, setManualCloseTarget] = useState<EligibleAccount | null>(null);
  const handleManualCloseClick = useCallback((row: EligibleAccount) => {
    setManualCloseTarget(row);
  }, []);

  // ----- Grid columns -----
  const columns: SyutGridColumn<EligibleAccount>[] = useMemo(() => [
    { id: 'caseCode',         header: 'Case #',          accessorKey: 'caseCode',         size: 130, enableSorting: true },
    { id: 'accountNumber',    header: 'Account #',       accessorKey: 'accountNumber',    size: 130, enableSorting: true },
    { id: 'customerName',     header: 'Customer',        accessorKey: 'customerName',     size: 200, enableSorting: true, flex: true },
    { id: 'productCode',      header: 'Product',         accessorKey: 'productCode',      size:  80, enableSorting: true,
      cell: ({ row }) => row.original.productCode ?? '-' },
    { id: 'branchCode',       header: 'Branch',          accessorKey: 'branchCode',       size: 100, enableSorting: true,
      cell: ({ row }) => row.original.branchCode ?? '-' },
    { id: 'currentDpd',       header: 'DPD (now)',       accessorKey: 'currentDpd',       size:  90, enableSorting: true },
    { id: 'openedDpd',        header: 'DPD (open)',      accessorKey: 'openedDpd',        size:  90, enableSorting: true },
    {
      id: 'currentOutstanding', header: 'Outstanding',   accessorKey: 'currentOutstanding', size: 130, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.currentOutstanding ?? 0).toLocaleString('en-IN')}`,
    },
    {
      id: 'isSecured', header: 'Secured', accessorKey: 'isSecured', size: 80, enableSorting: true,
      cell: ({ row }) => (
        <span style={{
          fontSize: '0.75rem', fontWeight: 600,
          color: row.original.isSecured ? '#16a34a' : '#6b7280',
          padding: '2px 8px',
          border: `1px solid ${row.original.isSecured ? '#16a34a' : '#6b7280'}`,
          borderRadius: '999px',
        }}>{row.original.isSecured ? 'Yes' : 'No'}</span>
      ),
    },
    {
      id: 'openedAt', header: 'Eligible Since', accessorKey: 'openedAt', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.openedAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return v; }
      },
    },
    { id: 'daysInPool', header: 'Days in Pool', accessorKey: 'daysInPool', size: 100, enableSorting: true },
    {
      id: 'openedEmiOutstandingCount', header: 'EMI O/S Count (Open)',
      accessorKey: 'openedEmiOutstandingCount', size: 110, enableSorting: true,
    },
    {
      id: 'openedEmiOutstandingAmount', header: 'EMI O/S Amount (Open)',
      accessorKey: 'openedEmiOutstandingAmount', size: 140, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.openedEmiOutstandingAmount ?? 0).toLocaleString('en-IN')}`,
    },
    {
      id: 'suggestedAgentName', header: 'Suggested Agent',
      accessorKey: 'suggestedAgentName', size: 140, enableSorting: false,
      cell: ({ row }) => row.original.suggestedAgentName ?? '—',
    },
  ], []);

  // ----- Row actions -----
  const getRowActions = useCallback((row: EligibleAccount): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];

    if (permissions.hasAction(PRELIT_POOL_MODULE, 'claim')) {
      items.push({
        id: 'claim',
        label: 'Open',
        icon: <IconSystem.checkCircle size={16} />,
        onClick: () => handleClaimClick(row),
      });
    }

    if (permissions.hasAction(PRELIT_POOL_MODULE, 'manual_close')) {
      items.push({
        id: 'manual-close',
        label: 'Manual Close',
        icon: <IconSystem.x size={16} />,
        onClick: () => handleManualCloseClick(row),
        variant: 'danger',
      });
    }

    return items;
  }, [permissions, handleClaimClick, handleManualCloseClick]);

  // ----- Action buttons -----
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (permissions.hasAction(PRELIT_POOL_MODULE, 'recompute_pool')) {
      buttons.push({
        id: 'recompute',
        icon: <IconSystem.checkCircle size={18} />,
        label: 'Re-evaluate Eligibility',
        tooltip: 'Re-evaluate eligibility for all accounts',
        onClick: handleRecompute,
        variant: 'icon',
        disabled: isRecomputing,
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Reload pool',
      onClick: refetch,
      variant: 'icon',
    });
    return buttons;
  }, [permissions, handleRecompute, isRecomputing, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Early Recovery Pool"
        description="Accounts eligible for pre-litigation action and waiting to be assigned. Newest cases first."
        icon={<IconSystem.list size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search account, customer, case #...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowActions={getRowActions}
                frozenRightColumns={['actions']}
                emptyMessage={
                  loading
                    ? 'Loading...'
                    : 'No accounts in the pool. Click Re-evaluate Eligibility to scan for newly eligible accounts.'
                }
              />
            )}

            {viewMode === 'grid' && (() => {
              const totalPages = Math.max(1, Math.ceil(data.length / gridPageSize));
              const safeGridPage = Math.min(gridPage, totalPages - 1);
              const pagedData = data.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
              return (
                <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    padding: '1rem', display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.25rem', flex: 1, overflow: 'auto', alignContent: 'start',
                  }}>
                    {pagedData.length === 0 && (
                      <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {loading ? 'Loading...' : 'No accounts in the pool.'}
                      </div>
                    )}
                    {pagedData.map((row) => (
                      <div
                        key={String(row.id)}
                        style={{
                          position: 'relative',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          background: 'var(--bg-primary)',
                          cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', gap: '0.5rem',
                        }}
                        onClick={() => permissions.hasAction(PRELIT_POOL_MODULE, 'claim') && handleClaimClick(row)}
                      >
                        {/* Action menu pinned to the card's top-right; stop click
                            propagation so opening the menu doesn't also fire the
                            card's onClick. */}
                        <div
                          style={{ position: 'absolute', top: 6, right: 6 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionMenu items={getRowActions(row)} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingRight: '1.5rem' }}>
                          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>{row.caseCode}</span>
                          <span style={{
                            fontSize: '0.7rem', fontWeight: 600,
                            color: row.isSecured ? '#16a34a' : '#6b7280',
                            border: `1px solid ${row.isSecured ? '#16a34a' : '#6b7280'}`,
                            padding: '1px 6px', borderRadius: '999px',
                          }}>{row.isSecured ? 'Secured' : 'Unsecured'}</span>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                          {row.customerName}
                          <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {row.accountNumber}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>DPD <strong style={{ color: 'var(--text-primary)' }}>{row.currentDpd}</strong></span>
                          <span>₹<strong style={{ color: 'var(--text-primary)' }}>{(row.currentOutstanding ?? 0).toLocaleString('en-IN')}</strong></span>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{row.productCode ?? '-'} · {row.branchCode ?? '-'}</span>
                          <span>{row.daysInPool}d in pool</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {data.length > gridPageSize && (
                    <div className="pagination-footer">
                      <button className="btn btn-secondary"
                        onClick={() => setGridPage((p) => Math.max(0, p - 1))}
                        disabled={safeGridPage === 0}>Prev</button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Page {safeGridPage + 1} / {totalPages}
                      </span>
                      <button className="btn btn-secondary"
                        onClick={() => setGridPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={safeGridPage >= totalPages - 1}>Next</button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Filter panel — sibling of grid wrapper. Conditional render (not class
            toggle) — there is no `.search-panel.collapsed` rule in CSS, so the
            class-toggle approach leaves the panel always visible. */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Product</label>
              <input
                type="text"
                className="form-input"
                value={productFilter}
                onChange={e => setProductFilter(e.target.value.toUpperCase())}
                placeholder="e.g. PL, HL, TWL..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>DPD min / max</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="number" className="form-input" value={dpdMin} onChange={e => setDpdMin(e.target.value)} placeholder="min"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <input type="number" className="form-input" value={dpdMax} onChange={e => setDpdMax(e.target.value)} placeholder="max"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Outstanding ₹ min / max</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="number" className="form-input" value={outstandingMin} onChange={e => setOutstandingMin(e.target.value)} placeholder="min"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <input type="number" className="form-input" value={outstandingMax} onChange={e => setOutstandingMax(e.target.value)} placeholder="max"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>EMI O/S Count min / max</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="number" className="form-input" value={emiCountMin} onChange={e => setEmiCountMin(e.target.value)} placeholder="min"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <input type="number" className="form-input" value={emiCountMax} onChange={e => setEmiCountMax(e.target.value)} placeholder="max"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>EMI O/S Amount ₹ min / max</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="number" className="form-input" value={emiAmountMin} onChange={e => setEmiAmountMin(e.target.value)} placeholder="min"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <input type="number" className="form-input" value={emiAmountMax} onChange={e => setEmiAmountMax(e.target.value)} placeholder="max"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Secured"
                options={[
                  { value: '', label: 'Any' },
                  { value: 'true', label: 'Secured' },
                  { value: 'false', label: 'Unsecured' },
                ]}
                value={securedFilter}
                onChange={(val) => setSecuredFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Claim Case dialog */}
      <ClaimCaseConfirmDialog
        case_={claimTarget}
        onCancel={() => setClaimTarget(null)}
        onClaimed={handleClaimed}
      />

      {manualCloseTarget && (
        <ManualCloseUnclaimedModal
          isOpen={!!manualCloseTarget}
          caseId={String(manualCloseTarget.id)}
          caseCode={manualCloseTarget.caseCode}
          customerName={manualCloseTarget.customerName}
          accountNumber={manualCloseTarget.accountNumber}
          onClose={() => setManualCloseTarget(null)}
          onClosed={() => { setManualCloseTarget(null); refetch(); }}
        />
      )}
    </div>
  );
};

export default PreLitigationPoolScreen;
